import { Home } from '@lmc/ts-common'
import { FormRadio, FormRadioGroup } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { getNominative } from 'common/utils'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { accessControlConstants } from 'locale/accessControl'
import { lowerCase } from 'lodash'
import { Divider, Switch } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import mobileImage from '../images/phone-illustration-SUlist.png'
import { Image } from 'antd'
import AccessControlPopover from './AccessControlPopover'
import useHomeType from 'hooks/useHomeType'
import RotaBasedAccessPopover from './RotaBasedAccessPopover'

interface AccessControlProps {
  showAllResidentsButton: boolean
  onShowAllResidentsButtonChange: (value: boolean) => void
  visitBasedAccessControl: boolean
  onVisitBasedAccessControlChange: (value: boolean) => void
}

export const AccessControl = ({
  showAllResidentsButton,
  onShowAllResidentsButtonChange,
  visitBasedAccessControl,
  onVisitBasedAccessControlChange,
}: AccessControlProps) => {
  const { t } = useTranslation(NAMESPACE.ACCESS_CONTROL)

  const { hasRota } = useHomeType()

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || '',
  )
  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const onAllResidentsButtonChange = (value: boolean) => {
    onShowAllResidentsButtonChange(value)
    onVisitBasedAccessControlChange(!value)
  }

  return (
    <Container>
      <AccessControlCard>
        <Header>
          <Title>
            <Heading level={3}>
              {t(accessControlConstants.TITLE_PREFERENCES, {
                nominative: peopleNominative,
              })}
            </Heading>
            <AccessControlPopover>
              <StyledQuestionCircleOutlined />
            </AccessControlPopover>
          </Title>
          <LightText>
            {t(accessControlConstants.ACCESS_CONTROL_DESCRIPTION, {
              nominative: lowerCase(peopleNominative),
            })}
          </LightText>
          <StyledDivider />
          <InfoSection>
            <Text level={2} weight="medium">
              {t(accessControlConstants.PRO_PLAN_INFO, {
                nominative: peopleNominative,
              })}
            </Text>
          </InfoSection>
          <FormSection>
            <LightText>
              {t(accessControlConstants.CHOSE_ACCESS_LEVEL)}
            </LightText>
            <FormRadioGroup
              value={showAllResidentsButton}
              onChange={(e) => onAllResidentsButtonChange(e.target.value)}
            >
              <FormRadio
                value={true}
                text={t(accessControlConstants.SHOW_ALL, {
                  nominative: peopleNominative,
                })}
              />
              <FormRadio
                value={false}
                text={t(accessControlConstants.ONLY_ASSIGNED, {
                  nominative: peopleNominative,
                })}
              />
            </FormRadioGroup>
          </FormSection>
          {hasRota && (
            <RotaSection>
              <StyledDivider />
              <RotaAccessControlCard>
                <Header>
                  <Title>
                    <Heading level={4}>
                      {t(accessControlConstants.ROTA_BASED_ACCESS)}
                    </Heading>
                    <RotaBasedAccessPopover>
                      <StyledQuestionCircleOutlined />
                    </RotaBasedAccessPopover>
                  </Title>
                  <StyledDivider />
                </Header>
                <Text>
                  {t(accessControlConstants.ROTA_BASED_ACCESS_DESCRIPTION, {
                    nominative: lowerCase(peopleNominative),
                  })}
                </Text>
                <StyledSwitch
                  checked={visitBasedAccessControl}
                  onChange={onVisitBasedAccessControlChange}
                  disabled={showAllResidentsButton}
                />
                <InfoSection>
                  <Text level={2} weight="medium">
                    {t(accessControlConstants.ROTA_BASED_ACCESS_INFO, {
                      nominative: peopleNominative,
                    })}
                  </Text>
                </InfoSection>
              </RotaAccessControlCard>
            </RotaSection>
          )}
        </Header>
      </AccessControlCard>
      <ImageContainer>
        <StyledImage src={mobileImage} alt="Mobile app" preview={false} />
        <OverlayText weight="medium">
          {t(accessControlConstants.MY_RESIDENTS, {
            nominative: peopleNominative,
          })}
        </OverlayText>
      </ImageContainer>
    </Container>
  )
}

const AccessControlCard = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px;
  border-radius: 16px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledDivider = styled(Divider)`
  margin: 0;
`
const LightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`
const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 18px;
`
const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const InfoSection = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  color: ${({ theme }) => theme.color.neutral.color4};
  padding: 12px 20px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
`
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const Container = styled.div`
  display: flex;
  gap: 50px;
`
const RotaAccessControlCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
  background-color: ${({ theme }) => theme.color.neutral.color10};
  padding: 24px;
  border-radius: 16px;
`
const RotaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledSwitch = styled(Switch)`
  width: 20px;
`
const ImageContainer = styled.div`
  width: 350px;
  flex-shrink: 0;
  position: relative;
`
const OverlayText = styled(Text)`
  color: ${({ theme }) => theme.palette.darkpurple};
  position: absolute;
  top: 84px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 1;
  white-space: nowrap;
`
const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`
